import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import ArticleCard from "@/components/base/ArticleCard";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb, { getArticleBreadcrumb } from "@/components/feature/Breadcrumb";

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6 text-center py-20">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
            <p className="mt-3 text-foreground-500 text-lg">文章不存在或已被删除</p>
            <Link
              to="/"
              className="mt-4 inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const cat = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = getArticleBreadcrumb(slug || "");

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "51黑料吃瓜网",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://hzljbj.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-6">
            {/* Header */}
            <header>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-block px-2.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                  {cat?.name || article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.date} {article.time}</span>
                <span className="text-xs text-foreground-500">作者：{article.author}</span>
                <span className="text-xs text-foreground-500">{article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读</span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-3xl text-foreground-900 leading-snug">
                {article.title}
              </h1>
            </header>

            {/* Featured Image */}
            <figure className="mt-6">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover rounded-lg"
                title={`51黑料 - ${article.title}`}
              />
            </figure>

            {/* Article Content */}
            <div
              className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-5 [&_p]:text-[15px] [&_p]:leading-[1.8]
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm text-foreground-500">分享到：</span>
              <a
                href={`https://weibo.com/share?url=https://hzljbj.cn/article/${article.slug}&title=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="nofollow noopener"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                aria-label="分享到微博"
              >
                <i className="ri-weibo-line" />
              </a>
              <a
                href={`https://connect.qq.com/widget/shareqq/index.html?url=https://hzljbj.cn/article/${article.slug}&title=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="nofollow noopener"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                aria-label="分享到QQ"
              >
                <i className="ri-qq-line" />
              </a>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 mt-12 pb-8 bg-background-100 py-8">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-heading text-xl text-foreground-900 mb-5 flex items-center gap-2">
                  <i className="ri-links-line text-accent-500" />
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => {
                    const raCat = categories.find((c) => c.id === ra.category);
                    return (
                      <ArticleCard
                        key={ra.id}
                        id={ra.id}
                        title={ra.title}
                        slug={ra.slug}
                        excerpt={ra.excerpt}
                        category={ra.category}
                        categoryName={raCat?.name || ra.category}
                        date={ra.date}
                        views={ra.views}
                        image={ra.image}
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}